import retry from 'retry';

export type PromiseBuilder<T> = () => Promise<Response>;
type OnRetry = (currentAttempt: number) => void;

export async function executeWithAuth0Retry<T>(builder: PromiseBuilder<T>, onRetry?: OnRetry): Promise<Response> {
  return new Auth0Retryer(builder, onRetry).execute();
}

export class Auth0Retryer<T> {
  private _onRetry?: OnRetry;
  private _success: boolean = false;
  private _attemptCount: number = 1;
  public readonly auditTrail: Array<{ attemptCount: number; attemptAt: number }> = [];
  public readonly builder: PromiseBuilder<T>;

  constructor(builder: PromiseBuilder<T>, onRetry?: OnRetry) {
    this.builder = builder;
    this._onRetry = onRetry;
  }

  public get attemptCount() {
    return this._attemptCount;
  }

  public get success() {
    return this._success;
  }

  public async execute(): Promise<Response> {
    return new Promise((resolve, reject) => {
      const operation = retry.operation({ retries: 4, randomize: true });
      operation.attempt(async currentAttempt => {
        try {
          this._audit();
          const response = await this.builder();
          if (response.ok) {
            this._success = true;
            operation.stop();
            resolve(response);
          } else {
            // The Promise returned from fetch() won’t reject on HTTP error status even if the response is an HTTP 404 or 500.
            // Instead, as soon as the server responds with headers, the Promise will resolve normally (with the ok property
            // of the response set to false if the response isn’t in the range 200–299), and it will only reject on network
            // failure or if anything prevented the request from completing.
            if (this._shouldHandleErr(response) && operation.retry(new Error())) {
              this._attemptCount++;
              this._onRetry?.(currentAttempt);
            } else {
              operation.stop();
              reject(new Error(response.statusText));
            }
          }
        } catch (err) {
          // That's outside of our jurisdiction
          operation.stop();
          reject(err);
        }
      });
    });
  }

  private _audit() {
    this.auditTrail.push({
      attemptCount: this.attemptCount,
      attemptAt: Date.now(),
    });
  }

  private _shouldHandleErr(response: Response) {
    return response?.status === 429;
  }
}
